import React, { Suspense, lazy, useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { persistStore } from 'redux-persist'
import store from './store/store'
import { AppRootContext, WindowContext } from './context'

import { Amplify } from 'aws-amplify'
import { currentConfig } from './services/aws/config'

import './index.scss'
import 'bootstrap-icons/font/bootstrap-icons.scss'

import 'react-datepicker/dist/react-datepicker.css'

import { initDatadogRum } from './observability'

export const isSektorEnv =
  import.meta.env.VITE_RETAIL && import.meta.env.VITE_RETAIL.trim() === 'sektor'

if (!isSektorEnv) import('./mikapaytech.scss')
if (isSektorEnv) import('./sektor.scss')

import { version } from '../package.json'
import { PageLoading } from './shared/components/Template/PageLoading'

const Navigation = lazy(() => import('./Navigation'))
const AlertList = lazy(() => import('./shared/components/alert/AlertList'))
const ModalContainer = lazy(
  () => import('./shared/components/modal/ModalContainer'),
)

export const appVersion = `Mika-Portal v${version}`

const persistor = persistStore(store)

initDatadogRum({
  applicationId: 'f8d961f6-45ad-478e-9dba-416d9d50f877',
  clientToken: 'pubcc2ebeaefac8f891d291d468ac07622d',
  service: 'mika-portal',
  env: import.meta.env.MODE,
  version,
})

const AmplifyInitializer = ({ children }: { children: React.ReactNode }) => {
  useEffect(() => {
    try {
      Amplify.configure(currentConfig)
    } catch (error) {
      console.error('Error configuring Amplify:', error)
    }
  }, [])

  return <>{children}</>
}

const App = () => {
  const portalRef = useRef<HTMLDivElement>(null)
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  )

  useEffect(() => {
    if (portalRef.current) setPortalContainer(portalRef.current)
  }, [])

  useEffect(() => {
    const themeClass = isSektorEnv ? 'theme-sektor' : 'theme-mikapaytech'
    document.body.classList.remove('theme-mikapaytech', 'theme-sektor')
    document.body.classList.add(themeClass)
  }, [])

  return (
    <WindowContext.Provider value={window}>
      <AppRootContext.Provider value={portalContainer}>
        <div id="portal-root" ref={portalRef} />
        <Navigation />
        <AlertList />
        <ModalContainer />
      </AppRootContext.Provider>
    </WindowContext.Provider>
  )
}

createRoot(document.getElementById('root')!).render(
  <Provider store={store}>
    <PersistGate loading={<PageLoading />} persistor={persistor}>
      <AmplifyInitializer>
        <Suspense fallback={<PageLoading />}>
          <App />
        </Suspense>
      </AmplifyInitializer>
    </PersistGate>
  </Provider>,
)
